import moment from 'moment'
import type { MapById } from 'shared/store/ecommerce/catalog'
import type { EcommOrder, PurchasableItem } from 'shared/store/ecommerce/order'
import { deliveryClient, ws } from 'shared/store/ws'
import type {
    CacMenu,
    CacOrder,
    CacProduct,
    CacPromotion,
    ClickAndCollectPromotion,
    Coupon,
    DlvOrder,
    OrderJobDriverResponse,
    OrderRestaurantShortDTO
} from 'shared/types'
import {
    addSecondsToGivenTime,
    catalogType,
    concatWithComma,
    couponStatus,
    couponType,
    deliveryDriverStatus,
    ecommerceType,
    ecommOrderStatus,
    formatDateToHoursAndMinutes,
    isDefined,
    orderPickUpType,
    orderStatus,
    platformHeader,
    promotionType,
    queryParam,
    regex,
    restaurantServices,
    restaurantStatus,
    subtractSecondsToGivenTime
} from 'shared/utils'
import { menuSize } from '../../const'

const {
    S: MENU_SIZE_S,
    M: MENU_SIZE_M,
    L: MENU_SIZE_L,
    XL: MENU_SIZE_XL
} = menuSize
const {
    ONSITE,
    TABLESERVICE,
    DRIVE,
    TAKEAWAY,
    PARKING,
    DELIVERY_IN_APP: PICK_UP_TYPE_DELIVERY_IN_APP,
    TABLE_ORDER
} = orderPickUpType
const { FOOD } = couponType
const { OPENED, IMMINENT_CLOSING } = restaurantStatus
const {
    CLICK_AND_COLLECT,
    CLICK_AND_COLLECT_DRIVE,
    CLICK_AND_COLLECT_ON_SITE,
    CLICK_AND_COLLECT_PICKUP_PARKING,
    DELIVERY_IN_APP,
    SERVICE_TABLE,
    TAKEAWAY: PICK_UP
} = restaurantServices
const {
    DEBITED,
    DEBIT_ERROR,
    DEBIT_PENDING,
    FINALIZED,
    PREPARATION_PENDING,
    PREPARED_NO_CALLBACK,
    PAID,
    PAID_VALIDATED,
    REBOOT_ERROR,
    REJECTED,
    STUART_ERROR,
    STUART_JOB_PENDING,
    STUART_JOB_VALIDATED,
    DELIVERY_ERROR,
    DELIVERY_JOB_PENDING,
    DELIVERY_JOB_VALIDATED,
    SYSTEM_CANCELLED,
    USER_CANCELLED,
    PAYMENT_PENDING
} = ecommOrderStatus

const {
    ACCEPTED,
    DISMISSED,
    DONE,
    INPROGRESS,
    READY,
    WAITING_FOR_PAYMENT,
    WAITING_RESPONSE
} = orderStatus

const {
    PENDING,
    PICKING,
    ALMOST_PICKING,
    WAITING_AT_PICKUP,
    DELIVERING,
    ALMOST_DELIVERING,
    WAITING_AT_DROPOFF,
    DELIVERED,
    CANCELLED
} = deliveryDriverStatus

const removeUrlPlatform = (url: string) => {
    const paramsObj = getUrlParams(url)
    delete paramsObj[queryParam.UPDATED_PLATFORM]
    const sortedParamsStr = Object.keys(paramsObj)
        .map(key => `${key}=${paramsObj[key]}`)
        .join('&')
    return `${url.split('?')[0]}${sortedParamsStr ? '?' : ''}${sortedParamsStr}`
}

const purchasableToCacOrderContent = (item: PurchasableItem) => {
    let orderContent = {
        id: item.id,
        quantity: item.quantity,
        originalPrice: item.originalPrice,
        finalPrice: item.finalPrice,
        label: item.label,
        freeItems: item.options,
        noIce: item.noIce,
        pickUpLater: item.pickUpLater,
        recipe:
            item.customizedIngredients &&
            //$FlowFixMe
            item.customizedIngredients.map(e => {
                const { points, ...customizedIngredient } = e
                return customizedIngredient
            }),
        promotions: item.promotions,
        airshipTagInApp: item.airshipTagInApp
    }
    if (item.steps) {
        orderContent = {
            ...orderContent,
            menuSize: item.menuSize || MENU_SIZE_M,
            //$FlowFixMe
            subContent: item.steps.map(step => {
                const {
                    originalPrice,
                    finalPrice,
                    ...cacOrderSubContent
                } = purchasableToCacOrderContent(step)
                return cacOrderSubContent
            })
        }
    }
    return orderContent
}

const isOrderPreparing = (orderStatus?: string): boolean => {
    return (
        orderStatus === DEBITED ||
        orderStatus === DEBIT_ERROR ||
        orderStatus === PREPARATION_PENDING ||
        orderStatus === PREPARED_NO_CALLBACK ||
        orderStatus === PAID_VALIDATED ||
        orderStatus === DEBIT_PENDING ||
        orderStatus === FINALIZED
    )
}

const isOrderDelivered = (
    orbStatus?: string,
    deliveredAt?: string,
    secondsBeforeOrderDelivered: number
): boolean => {
    if (isDefined(deliveredAt)) {
        if (orbStatus === orderStatus.DONE) {
            // deliveredAt is checked with the isDefined method but flow does not consider it a proper check
            //$FlowFixMe
            const deliveredTime = new Date(deliveredAt).getTime()
            const currentTime = new Date().getTime()
            const timeDifferenceInSeconds = (currentTime - deliveredTime) / 1000
            return timeDifferenceInSeconds > secondsBeforeOrderDelivered
        }
        return false
    }
    return orbStatus === orderStatus.DONE
}

const isOrderCancelled = (orderStatus?: string, orbStatus?: string) => {
    return (
        orbStatus === DISMISSED ||
        orderStatus === REJECTED ||
        orderStatus === SYSTEM_CANCELLED ||
        orderStatus === USER_CANCELLED ||
        orderStatus === REBOOT_ERROR
    )
}

const isOrderCancelledByUser = (orderStatus?: string) => {
    return orderStatus === USER_CANCELLED
}

const isOrderLaunched = (orderStatus?: string, orbStatus?: string) => {
    return (
        (orbStatus &&
            orbStatus !== ACCEPTED &&
            orbStatus !== WAITING_FOR_PAYMENT) ||
        isOrderPreparing(orderStatus)
    )
}

const isLaunchedOrderWaitingForResponse = (orbStatus?: string) => {
    return orbStatus === WAITING_RESPONSE
}
const isLaunchedOrderPreparing = (orbStatus?: string, orderStatus?: string) => {
    return (
        orbStatus === INPROGRESS ||
        (orderStatus === PREPARED_NO_CALLBACK && !orbStatus)
    )
}
const isLaunchedOrderReadyForCollect = (
    orbStatus?: string,
    isDelivered?: boolean
) => {
    return orbStatus === READY || (orbStatus === DONE && !isDelivered)
}

const isDeliveryOrderPaid = (orderStatus?: string) => {
    return [
        DELIVERY_JOB_PENDING,
        DELIVERY_JOB_VALIDATED,
        PREPARATION_PENDING,
        PREPARED_NO_CALLBACK,
        DEBIT_PENDING,
        DEBIT_ERROR,
        DEBITED,
        FINALIZED,
        PAID,
        PAID_VALIDATED
    ].includes(orderStatus)
}

const isDeliveryAlmostDelivering = (orderStatus?: string) => {
    return [ALMOST_DELIVERING].includes(orderStatus)
}

const isDeliveryOrderWaitingForValidation = (orderStatus?: string) => {
    return (
        orderStatus === PAID ||
        orderStatus === STUART_JOB_PENDING ||
        orderStatus === STUART_JOB_VALIDATED ||
        orderStatus === DELIVERY_JOB_PENDING ||
        orderStatus === DELIVERY_JOB_VALIDATED ||
        orderStatus === PAYMENT_PENDING ||
        isOrderPreparing(orderStatus)
    )
}
const isWaitingForDriver = (orderStatus?: string, deliveryStatus?: string) => {
    return (
        (orderStatus === DELIVERY_JOB_VALIDATED ||
            orderStatus === STUART_JOB_VALIDATED) &&
        deliveryStatus === PENDING
    )
}

const isDeliveryOrderPreparingInRestaurant = (deliveryStatus?: string) => {
    return (
        deliveryStatus === PICKING ||
        deliveryStatus === ALMOST_PICKING ||
        deliveryStatus === WAITING_AT_PICKUP
    )
}

const isDeliveryOrderDelivering = (deliveryStatus?: string) => {
    return deliveryStatus === DELIVERING || deliveryStatus === ALMOST_DELIVERING
}

const isDeliveryOrderDelivered = (deliveryStatus?: string) => {
    return deliveryStatus === DELIVERED || deliveryStatus === WAITING_AT_DROPOFF
}

const isDeliveryOrderCancelled = (
    deliveryStatus?: string,
    orderStatus?: string
) => {
    return (
        deliveryStatus === CANCELLED ||
        orderStatus === REJECTED ||
        orderStatus === SYSTEM_CANCELLED ||
        orderStatus === USER_CANCELLED ||
        orderStatus === REBOOT_ERROR ||
        orderStatus === STUART_ERROR ||
        orderStatus === DELIVERY_ERROR
    )
}
const isDeliveryDriverAtDropOff = (deliveryStatus?: string) => {
    return deliveryStatus === WAITING_AT_DROPOFF
}

const isDeliveryOrderWaitingForDriverToPickUp = (
    orbStatus?: string,
    deliveryStatus?: string
) => {
    return (
        ((orbStatus === READY || orbStatus === DELIVERED) &&
            deliveryStatus === PENDING) ||
        deliveryStatus === PICKING ||
        deliveryStatus === ALMOST_PICKING ||
        deliveryStatus === WAITING_AT_PICKUP
    )
}

const getDropOffEtaKey = (deliveryStatus?: string) => {
    const isDlvOrderDelivered = isDeliveryOrderDelivered(deliveryStatus)
    return isDlvOrderDelivered
        ? 'ecommerce.delivery.eta.title.delivered'
        : 'ecommerce.delivery.eta.title.delivering'
}

const getDeliveryParams = (
    order: DlvOrder,
    ordersJobs?: { [string]: Array<OrderJobDriverResponse> }
) => {
    let deliveryParams
    if (order.pickUpType === PICK_UP_TYPE_DELIVERY_IN_APP) {
        const {
            creationDate,
            deliveryAddress,
            id,
            deliveryStatus: dlvStatus,
            restaurant
        } = order
        const { higherRangeArrival, lowerRangeArrival } = restaurant || {}
        let deliveryStatus,
            higherArrivalRange,
            lowerArrivalRange,
            higherDeliveryArrivalRange,
            lowerDeliveryArrivalRange,
            trackingUrl
        const hasArrivalRange =
            isDefined(higherRangeArrival) && isDefined(lowerRangeArrival)
        if (hasArrivalRange) {
            higherArrivalRange = formatDateToHoursAndMinutes(
                // $FlowFixMe
                addSecondsToGivenTime(creationDate, higherRangeArrival)
            )
            lowerArrivalRange = formatDateToHoursAndMinutes(
                // $FlowFixMe
                addSecondsToGivenTime(creationDate, lowerRangeArrival)
            )
        }
        const orderJobs = ordersJobs && ordersJobs[id]
        orderJobs &&
            orderJobs.map(orderJob => {
                if (orderJob) {
                    if (isDefined(orderJob.dropoffEta)) {
                        higherDeliveryArrivalRange = formatDateToHoursAndMinutes(
                            // $FlowFixMe
                            addSecondsToGivenTime(orderJob.dropoffEta, 5 * 60)
                        )
                        lowerDeliveryArrivalRange = formatDateToHoursAndMinutes(
                            // $FlowFixMe
                            subtractSecondsToGivenTime(
                                orderJob.dropoffEta,
                                5 * 60
                            )
                        )
                    }
                    deliveryStatus = orderJob.deliveryStatus
                    trackingUrl = orderJob.trackingUrl
                }
            })
        deliveryParams = {
            deliveryAddress,
            deliveryStatus: deliveryStatus || dlvStatus,
            higherArrivalRange,
            lowerArrivalRange,
            higherDeliveryArrivalRange,
            lowerDeliveryArrivalRange,
            trackingUrl
        }
    }
    return deliveryParams
}

const displayDeliveryDriverMap = (
    deliveryStatus?: string,
    trackingUrl?: string
) => {
    return isDeliveryOrderDelivering(deliveryStatus) && !!trackingUrl
}

const orderMeta = (order: CacOrder, event_value?: string): any => {
    let meta = {}

    const computePromotions = (
        promotions: Array<ClickAndCollectPromotion>,
        defaultCouponValue?: number
    ) => {
        promotions.forEach(promo => {
            meta.context.coupon = concatWithComma(
                meta.context.coupon,
                promo.promotionName || ''
            )
            meta.context.coupon_id = concatWithComma(
                meta.context.coupon_id,
                promo.promotionId || ''
            )
            meta.context.coupon_value = concatWithComma(
                meta.context.coupon_value,
                getPromotionValue(promo) ||
                    (defaultCouponValue && defaultCouponValue.toString()) ||
                    ''
            )
        })
    }

    if (order && order.content) {
        const quantity = order.content.reduce(
            (acc, item) => acc + item.quantity,
            0
        )
        meta = {
            event_action: '',
            event_value,
            event_category: '',
            event_label: '',
            context: {
                transaction_id: order.id,
                quantity,
                payment_type: order.paymentType,
                creditcard_type: order.creditCardType,
                card_registered: order.cardRegistered,
                restaurant_id: order.restaurant ? order.restaurant.id : '',
                restaurant_name: order.restaurant ? order.restaurant.name : '',
                price: order.finalPrice,
                value: order.finalPrice,
                currency: 'EUR',
                collect_choice: order.pickUpType,
                item_name: '',
                item_id: '',
                item_price: '',
                coupon: '',
                coupon_id: '',
                coupon_value: ''
            }
        }

        order.content &&
            order.content.forEach(item => {
                meta.context.item_name = concatWithComma(
                    meta.context.item_name,
                    item.label || ''
                )
                meta.context.item_id = concatWithComma(
                    meta.context.item_id,
                    item.id || ''
                )
                meta.context.item_price = concatWithComma(
                    meta.context.item_price,
                    ((item.finalPrice || 0) * (item.quantity || 0)).toString()
                )
                item.promotions &&
                    computePromotions(item.promotions, item.finalPrice)
            })

        order.promotions && computePromotions(order.promotions)

        return meta
    }
}

const orderAppsFlyerData = (order: CacOrder): any => {
    const { content, finalPrice, id } = order || {}
    let contentTypes = ''
    let contentIds = ''
    let quantities = ''
    if (content) {
        content.forEach(content => {
            const { label, id, quantity } = content
            if (label) {
                contentTypes = concatWithComma(label, contentTypes)
            }
            if (id) {
                contentIds = concatWithComma(id, contentIds)
            }
            if (quantity) {
                quantities = concatWithComma(quantity.toString(), quantities)
            }
        })
    }
    return {
        af_revenue: finalPrice,
        af_order_id: id,
        af_content_type: contentTypes,
        af_content_id: contentIds,
        af_quantity: quantities
    }
}

const displayCollectModeSubtitle = (collectMode: string) => {
    switch (collectMode) {
        case TABLESERVICE:
        case ONSITE:
            return 'clickandcollect.delivery.choice.onsite'
        case TAKEAWAY:
            return 'clickandcollect.delivery.choice.takeaway'
        case DRIVE:
            return 'clickandcollect.delivery.choice.drive'
        case PARKING:
            return 'clickandcollect.delivery.choice.parking'
        case TABLE_ORDER:
            return 'clickandcollect.delivery.choice.tableorder.withTable'
        default:
            return undefined
    }
}

const getOrderStatus = (
    isDelivered: boolean,
    isCancelled: boolean,
    isDelivery?: boolean
) => {
    return isDelivered
        ? isDelivery
            ? 'orb.delivery.card.order.status.done'
            : 'orb.card.order.status.done'
        : isCancelled
        ? isDelivery
            ? 'orb.card.delivery.order.status.cancelled'
            : 'orb.card.order.status.cancelled'
        : null
}

const getDisplayableOrders = (
    orders: { [string]: EcommOrder },
    isDesktop: boolean
) => {
    const sortedOrders = (Object.values(orders): any).sort(
        (o1: EcommOrder, o2: EcommOrder) => {
            return moment(o2.creationDate).diff(moment(o1.creationDate))
        }
    )
    const displayableOrders = sortedOrders.filter((order: EcommOrder) => {
        if (isDesktop && order.pickUpType === PARKING) {
            return false
        }
        if (order?.orderStatus) {
            return order.hasBeenPaid
        }
        return true
    })
    return displayableOrders
}

const getFilteredCoupons = (
    coupons?: Array<Coupon>,
    restaurant?: OrderRestaurantShortDTO,
    collectMode?: string,
    idsCouponsTemporaryBurned: Array<string>
): Array<Coupon> =>
    coupons
        ? coupons.filter(
              (c: Coupon) =>
                  c.status === couponStatus.VALID &&
                  (c.restaurants.length === 0 ||
                      c.restaurants.includes('Tous') ||
                      (restaurant &&
                          c.restaurants.includes(restaurant.name))) &&
                  !idsCouponsTemporaryBurned.includes(c.id) &&
                  collectMode &&
                  c.canals &&
                  c.canals.includes(collectMode) &&
                  c.type === FOOD
          )
        : []

const getUrlParams = (url: string): { [string]: any } => {
    let params = {}
    url.replace(regex.URL_PARAMS, (m, key, value) => {
        params[key] = value
        return value
    })
    return params
}

const sortUrlParams = (url: string) => {
    const paramsObj = getUrlParams(url)
    const sortedParamsStr = Object.keys(paramsObj)
        .sort()
        .map(key => `${key}=${paramsObj[key]}`)
        .join('&')
    return `${url.split('?')[0]}${sortedParamsStr ? '?' : ''}${sortedParamsStr}`
}

const getIsFromApp = (params: { [string]: any }) => {
    const param = params[queryParam.UPDATED_PLATFORM]
    return param === platformHeader.APP_IOS || param === platformHeader.APP_AND
}

const getPromotionValue = (promotion: ClickAndCollectPromotion): string => {
    const { promoType, promoValue = '' } = promotion
    switch (promoType) {
        case promotionType.ITEM_PERCENT_DISCOUNT:
        case promotionType.ORDER_PERCENT_DISCOUNT:
            return `-${promoValue}%`
        case promotionType.ITEM_PERCENT_FIXED:
        case promotionType.ORDER_PERCENT_FIXED:
            return `${promoValue}%`
        case promotionType.ITEM_VALUE_DISCOUNT:
        case promotionType.ORDER_VALUE_DISCOUNT:
            return `-${promoValue}€`
        case promotionType.ITEM_VALUE_FIXED:
        case promotionType.ORDER_VALUE_FIXED:
            return `${promoValue}€`
        default:
            return ''
    }
}

const getService = (collectMode: string) => {
    switch (collectMode) {
        case DRIVE:
            return CLICK_AND_COLLECT_DRIVE
        case ONSITE:
            return CLICK_AND_COLLECT_ON_SITE
        case PARKING:
            return CLICK_AND_COLLECT_PICKUP_PARKING
        case TABLESERVICE:
            return SERVICE_TABLE
        case TABLE_ORDER:
            return TABLE_ORDER
        default:
            return PICK_UP
    }
}

const getMostRecentOrder = (orders: { [string]: EcommOrder }) => {
    let mostRecentOrders = (Object.values(orders): any)
        .filter((order: EcommOrder) => order.hasBeenPaid)
        .sort((o1: EcommOrder, o2: EcommOrder) => {
            return moment(o2.creationDate).diff(moment(o1.creationDate))
        })
    if (mostRecentOrders.length > 0) {
        return mostRecentOrders[0]
    }
}

const isCreditCardValid = (expirationDate: string) => {
    const expMonth = parseInt(expirationDate.split('/')[0], 10)
    const expYear = parseInt(expirationDate.split('/')[1], 10)

    const currentMonth = new Date().getMonth() + 1
    const year = new Date().getFullYear().toString()
    const currentYear = parseInt(
        year.substring(year.length - 2, year.length),
        10
    )

    return (
        expYear > currentYear ||
        (expYear === currentYear && expMonth >= currentMonth)
    )
}

const promotionsAppliedInCart = (products: Array<PurchasableItem>) => {
    const promotions = []
    products.forEach(product => {
        product.promotions &&
            product.promotions.forEach(promotion => {
                promotions.push(promotion)
            })
    })
    return promotions
}

const isKingdomPointsCoupon = (couponCode: string) => {
    return couponCode[0].toUpperCase() === 'P' && couponCode.length === 8
}

const isLotteryCoupon = (couponCode: string) => {
    return couponCode[0].toUpperCase() === 'C' && couponCode.length === 8
}

const isTuPreferesKingCoupon = (couponCode: string) => {
    // TODO - Modified for BE-8085 - Uncomment when TPK returns
    return false //couponCode[0].toUpperCase() === 'T' && couponCode.length === 7
}

const cartHasPromotionsInvalidForGames = (
    promotions: Array<ClickAndCollectPromotion>,
    products: Array<PurchasableItem>
) => {
    return (
        promotions.length > 0 ||
        Boolean(
            products.find(
                e =>
                    e.type !== catalogType.GAME &&
                    e.promotions &&
                    e.promotions.length > 0
            )
        )
    )
}

export const getMenuNameFromSize = (size?: string) => {
    switch (size) {
        case MENU_SIZE_S:
            return 'clickandcollect.catalog.menu.size.small'
        case MENU_SIZE_L:
            return 'clickandcollect.catalog.menu.size.large'
        case MENU_SIZE_XL:
            return 'clickandcollect.catalog.menu.size.extra.large'

        default:
            return 'clickandcollect.catalog.menu.size.medium'
    }
}

const isEcommerceModeActive = (
    restaurant: OrderRestaurantShortDTO,
    ecommerceMode?: string
) => {
    // Property dlvStatus is missing in RestaurantShortDTO, but it can also have the type DlvRestaurantShortDTO
    const { cacStatus, dlvStatus, services } = restaurant
    if (services) {
        const isDelivery = ecommerceMode === ecommerceType.DELIVERY_IN_APP
        const ecommerceStatus = isDelivery ? dlvStatus : cacStatus
        const ecommerceService = isDelivery
            ? DELIVERY_IN_APP
            : CLICK_AND_COLLECT

        return (
            (ecommerceStatus === OPENED ||
                ecommerceStatus === IMMINENT_CLOSING) &&
            services.includes(ecommerceService)
        )
    }
    return false
}

const isDriveActive = (restaurant: OrderRestaurantShortDTO) => {
    const { cadStatus, services } = restaurant
    if (services) {
        return (
            (cadStatus === OPENED || cadStatus === IMMINENT_CLOSING) &&
            services.includes(CLICK_AND_COLLECT_DRIVE)
        )
    }
    return false
}

const getEcommerceAxiosClient = (ecommerceMode: string) => {
    if (ecommerceMode === ecommerceType.DELIVERY_IN_APP) {
        return deliveryClient
    }
    return ws
}

const getCurrentOrder = (
    cacOrders: { [string]: CacOrder },
    deliveryOrders: { [string]: DlvOrder },
    token?: string
) => {
    const currentCacOrder = cacOrders && token && cacOrders[token]
    const currentDeliveryOrder =
        deliveryOrders && token && deliveryOrders[token]
    return currentCacOrder || currentDeliveryOrder
}

const getPickUpTypeKey = (pickUpType: string) => {
    switch (pickUpType) {
        case ONSITE:
        case TABLESERVICE:
            return 'clickandcollect.delivery.choice.onsite'
        case TAKEAWAY:
            return 'clickandcollect.delivery.choice.takeaway'
        case DRIVE:
            return 'clickandcollect.delivery.choice.drive'
        case PARKING:
            return 'clickandcollect.delivery.choice.parking'
        case PICK_UP_TYPE_DELIVERY_IN_APP:
            return 'ecommerce.delivery'
        case TABLE_ORDER:
            return 'clickandcollect.delivery.choice.tableorder'
        default:
            return ''
    }
}

const hasAvailableItem = (
    itemIds: Array<string>,
    itemsById: MapById<CacMenu | CacProduct>
) =>
    itemIds.find((itemId: string) => {
        const item = itemsById[itemId]
        return !!(item?.available && item?.active)
    })

const doesPromotionHaveAvailableProductsOrMenus = (
    promotionId: string,
    menusById: MapById<CacMenu>,
    productsById: MapById<CacProduct>,
    promotionsById: MapById<CacPromotion>
) => {
    const promotion = promotionsById[promotionId] || {}
    const { menuIds, productIds } = promotion || {}
    let hasAvailableMenus, hasAvailableProducts
    if (menuIds && menuIds.length > 0) {
        //$FlowFixMe
        hasAvailableMenus = hasAvailableItem(menuIds, menusById)
    }
    if (productIds && productIds.length > 0) {
        //$FlowFixMe
        hasAvailableProducts = hasAvailableItem(productIds, productsById)
    }
    return hasAvailableMenus || hasAvailableProducts
}

export {
    displayCollectModeSubtitle,
    getDisplayableOrders,
    getFilteredCoupons,
    getMostRecentOrder,
    getOrderStatus,
    getPromotionValue,
    getService,
    getUrlParams,
    sortUrlParams,
    getIsFromApp,
    isCreditCardValid,
    isOrderPreparing,
    isOrderCancelled,
    isOrderCancelledByUser,
    isOrderLaunched,
    isOrderDelivered,
    isLaunchedOrderWaitingForResponse,
    isLaunchedOrderPreparing,
    isLaunchedOrderReadyForCollect,
    isDeliveryOrderWaitingForValidation,
    isDeliveryOrderPreparingInRestaurant,
    isDeliveryOrderDelivering,
    isDeliveryOrderDelivered,
    isDeliveryOrderCancelled,
    isDeliveryDriverAtDropOff,
    isDeliveryOrderWaitingForDriverToPickUp,
    getDropOffEtaKey,
    getDeliveryParams,
    displayDeliveryDriverMap,
    orderAppsFlyerData,
    orderMeta,
    purchasableToCacOrderContent,
    promotionsAppliedInCart,
    isKingdomPointsCoupon,
    cartHasPromotionsInvalidForGames,
    isEcommerceModeActive,
    isDriveActive,
    getEcommerceAxiosClient,
    getCurrentOrder,
    getPickUpTypeKey,
    doesPromotionHaveAvailableProductsOrMenus,
    isLotteryCoupon,
    isTuPreferesKingCoupon,
    isWaitingForDriver,
    removeUrlPlatform,
    isDeliveryOrderPaid,
    isDeliveryAlmostDelivering
}
